<template>
  <div :class="['bet-zone-info', { reverse: isReverse }]">
    <div class="banker-info">
      <div class="total-bet">
        <div class="left">
          <RadialProgressBar
            :diameter="40"
            :strokeWidth="3"
            :innerStrokeWidth="3"
            :startColor="'#ffbfbf'"
            :stopColor="'#ffbfbf'"
            :innerStrokeColor="'#043417'"
            :animateSpeed="1000"
            :completed-steps="bankerRatio"
            :total-steps="100"
            :isClockwise="true"
          >
            {{ bankerRatio }}%
          </RadialProgressBar>
        </div>
        <div class="right">
          <div class="total-bet-amount" :class="{ 'large-amount': isLargeAmount }">{{ currencySymbol }} {{ isLargeAmount ? fla(valueAfterExchange(banker.totalBetAmount)) : fa(valueAfterExchange(banker.totalBetAmount)) }}</div>
          <div class="total-bet-count">
            <PersonIcon /><span>{{ banker.totalBetCount }}</span>
          </div>
        </div>
      </div>
      <div class="odds">{{ banker.odds }}x</div>
      <div class="owner-bet">
        <div class="label">{{ $t('Banker') }}</div>
        <div class="confirmed-bet">{{ currencySymbol }} {{ fa(myBets.banker) }}</div>
      </div>
    </div>
    <div class="draw-info">
      <div class="odds">
        <div class="draw-odds-label">{{ $t('Draw') }}</div>
        <div>{{ draw.odds }}x</div>
      </div>
      <div class="confirmed-bet">{{ currencySymbol }} {{ fa(myBets.draw) }}</div>
    </div>
    <div class="player-info">
      <div class="total-bet">
        <div class="left">
          <RadialProgressBar
            :diameter="40"
            :strokeWidth="3"
            :innerStrokeWidth="3"
            :startColor="'#93c4ff'"
            :stopColor="'#93c4ff'"
            :innerStrokeColor="'#043417'"
            :animateSpeed="1000"
            :completed-steps="playerRatio"
            :total-steps="100"
            :isClockwise="true"
          >
            {{ playerRatio }}%
          </RadialProgressBar>
        </div>
        <div class="right">
          <div class="total-bet-amount" :class="{ 'large-amount': isLargeAmount }">{{ currencySymbol }} {{ isLargeAmount ? fla(valueAfterExchange(player.totalBetAmount)) : fa(valueAfterExchange(player.totalBetAmount)) }}</div>
          <div class="total-bet-count">
            <PersonIcon role="player" /><span>{{ player.totalBetCount }}</span>
          </div>
        </div>
      </div>
      <div class="odds">{{ player.odds }}x</div>
      <div class="owner-bet">
        <div class="label">{{ $t('Player') }}</div>
        <div class="confirmed-bet">{{ currencySymbol }} {{ fa(myBets.player) }}</div>
      </div>
    </div>
  </div>
</template>
<script>
import PersonIcon from '@/components/bet-panel/bet-zone/person.vue';
import RadialProgressBar from '@/components/bet-panel/bet-zone/radial-progress-bar.vue';
import { mapGetters } from 'vuex';
import { formatAmount, mathFloor, formatLargeAmount } from '@/utils/helpers';
import { find, isEmpty } from 'lodash';

const defaultSelection = {
  name: '',
  displayName: '',
  odds: 0,
  totalBetAmount: 0,
  totalBetCount: 0,
};

export default {
  components: {
    PersonIcon,
    RadialProgressBar,
  },
  computed: {
    ...mapGetters({
      currentFight: 'fight/fight',
      myBets: 'bets/myBets',
      member: 'general/member',
      arena: 'general/selectedArena',
    }),
    isReverse() {
      return this.arena.isReverse;
    },
    currencySymbol() {
      return this.member.currencySymbol;
    },
    banker() {
      const banker = find(this.currentFight.selections, (s) => s.name === 'banker');
      return banker && !isEmpty(banker) ? banker : defaultSelection;
    },
    draw() {
      const draw = find(this.currentFight.selections, (s) => s.name === 'draw');
      return draw && !isEmpty(draw) ? { ...draw, odds: Number(draw.odds) + 1 } : defaultSelection;
    },
    player() {
      const player = find(this.currentFight.selections, (s) => s.name === 'player');
      return player && !isEmpty(player) ? player : defaultSelection;
    },
    totalBet() {
      return Number(this.banker.totalBetAmount) + Number(this.player.totalBetAmount);
    },
    bankerRatio() {
      return this.totalBet <= 0 ? 0 : parseInt(((this.banker.totalBetAmount / this.totalBet) * 100).toFixed(0));
    },
    playerRatio() {
      return this.totalBet <= 0 ? 0 : parseInt(((this.player.totalBetAmount / this.totalBet) * 100).toFixed(0));
    },
    isLargeAmount() {
      return this.valueAfterExchange(this.banker.totalBetAmount) > 999999999.99 ||
             this.valueAfterExchange(this.player.totalBetAmount) > 999999999.99 
    }
  },
  methods: {
    fa(value) {
      return formatAmount(value, 0);
    },
    fla(value) {
      return formatLargeAmount(value, 0);
    },
    valueAfterExchange(value) {
      const { exchangeRate } = this.member;
      return mathFloor(Number(value) * Number(exchangeRate));
    },
  },
};
</script>
<style lang="scss" scoped>
.bet-zone-info {
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 80%;
  font-size: 14px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 16px;
  box-sizing: border-box;
  text-transform: uppercase;
  transform: translateY(-50%);

  .noselect {
    @include user-select-none();
  }

  .total-bet-amount{
    &.large-amount {
      font-size: 16px;
      font-weight: 700;
    }
  }

  .banker-info,
  .player-info,
  .draw-info {
    text-align: left;
    color: #ffbfbf;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-around;

    .total-bet {
      display: flex;
      flex-direction: row;
      align-items: center;

      .left {
        background-color: #043417;
        border-radius: 50%;
        margin-right: 5px;

        .radial-progress-inner {
          font-size: 6px;
        }
      }

      .right {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-evenly;

        .total-bet-count {
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;

          span {
            display: flex;
            align-self: flex-end;
            line-height: 11px;
          }
        }
      }
    }

    .odds {
      font-size: 22px;
      color: #fff;
      text-transform: none;
      user-select: none;
    }

    .owner-bet {
      color: #fff;
    }

    .confirmed-bet {
      color: #ffd688;
      font-size: 13px;
    }
  }

  .draw-info {
    text-align: center;
    color: #d3ffbf;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);

    .label {
      color: #fff;
    }

    .draw-odds-label {
      text-transform: uppercase;
    }
  }

  .player-info {
    text-align: right;
    color: #93c4ff;

    .total-bet {
      flex-direction: row-reverse;

      .left {
        margin-left: 5px;
        margin-right: 0;
      }

      .total-bet-count {
        justify-content: flex-end !important;
      }
    }
  }

  &.reverse {
    flex-direction: row-reverse;

    .player-info {
      text-align: left;

      .total-bet {
        flex-direction: row;
        .right {
          .total-bet-count {
            justify-content: flex-start !important;
          }
        }
        .left {
          margin-right: 5px;
          margin-left: 0;
        }
      }
    }

    .banker-info {
      text-align: right;

      .total-bet {
        flex-direction: row-reverse;

        .right {
          .total-bet-count {
            justify-content: flex-end;
          }
        }

        .left {
          margin-left: 5px;
          margin-right: 0;
        }
      }
    }
  }
}
</style>
